import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Users, Briefcase, FileText, Calendar, CheckCircle, DollarSign, Download, FileSpreadsheet, LayoutGrid, List, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import DateRangeFilter from '../components/DateRangeFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Recruiters() {
  const [performance, setPerformance] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [viewMode, setViewMode] = useState('card');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const fetchPerformance = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/recruiters/performance?time_filter=${timeFilter}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setPerformance(response.data);
    } catch (error) { toast.error('Failed to fetch recruiter performance'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (timeFilter !== 'custom') fetchPerformance(); }, [timeFilter]);

  const isAdminUser = user?.role === 'admin';
  const canExport = user && (user.role === 'admin' || user.role === 'team_leader');

  const totals = useMemo(() => {
    return performance.reduce((acc, r) => ({
      positions: acc.positions + (r.positions || 0),
      resumes: acc.resumes + (r.resumes || 0),
      interviews: acc.interviews + (r.interviews || 0),
      closures: acc.closures + (r.closures || 0),
      joinings: acc.joinings + (r.joinings || 0),
      revenue: acc.revenue + (r.revenue || 0),
    }), { positions: 0, resumes: 0, interviews: 0, closures: 0, joinings: 0, revenue: 0 });
  }, [performance]);

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/export/recruiter-performance?format=${format}&time_filter=${timeFilter}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = blobUrl; link.setAttribute('download', `recruiter_performance.${ext}`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(blobUrl);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) { toast.error('Failed to export report'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><p className="text-slate-500">Loading performance data...</p></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-blue-900 mb-1" data-testid="recruiters-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Recruiter Analytics</h1>
          <p className="text-slate-500 text-sm">Individual performance metrics & target tracking</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-white border border-blue-200 rounded overflow-hidden">
            <button data-testid="view-card" onClick={() => setViewMode('card')} className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${viewMode === 'card' ? 'bg-blue-600 text-white' : 'text-blue-700 hover:bg-blue-50'}`}><LayoutGrid size={14} />Cards</button>
            <button data-testid="view-table" onClick={() => setViewMode('table')} className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-blue-700 hover:bg-blue-50'}`}><List size={14} />Table</button>
          </div>
          <DateRangeFilter timeFilter={timeFilter} setTimeFilter={setTimeFilter} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} onApplyRange={fetchPerformance} />
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-recruiters-button" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50"><Download size={16} className="mr-2" />Export</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-recruiters-excel" onClick={() => handleExport('excel')}><FileSpreadsheet size={16} className="mr-2" />Excel</DropdownMenuItem>
                <DropdownMenuItem data-testid="export-recruiters-pdf" onClick={() => handleExport('pdf')}><FileText size={16} className="mr-2" />PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {performance.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center">
          <Users size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">No recruiter data available for this period.</p>
        </div>
      ) : viewMode === 'card' ? (
        /* Card View */
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {performance.map((recruiter) => (
              <div key={recruiter.recruiter_id} data-testid={`recruiter-card-${recruiter.recruiter_id}`} className="bg-white border border-blue-100 rounded p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">{recruiter.recruiter_name.charAt(0)}</div>
                  <div>
                    <h3 className="text-xl font-bold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{recruiter.recruiter_name}</h3>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Recruiter</p>
                    {recruiter.team_name && <p className="text-xs text-indigo-600 font-semibold mt-1">{recruiter.team_name}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-sm">
                    <Briefcase size={20} className="mx-auto mb-2 text-indigo-600" />
                    <p className="text-2xl font-extrabold text-slate-900">{recruiter.positions}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Positions</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-sm">
                    <FileText size={20} className="mx-auto mb-2 text-pink-600" />
                    <p className="text-2xl font-extrabold text-slate-900">{recruiter.resumes}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Resumes</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-sm">
                    <Calendar size={20} className="mx-auto mb-2 text-amber-600" />
                    <p className="text-2xl font-extrabold text-slate-900">{recruiter.interviews}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Interviews</p>
                  </div>
                </div>
                <div className={`grid ${isAdminUser ? 'grid-cols-3' : 'grid-cols-2'} gap-4 mt-4`}>
                  <div className="text-center p-3 bg-slate-50 rounded-sm">
                    <CheckCircle size={20} className="mx-auto mb-2 text-emerald-600" />
                    <p className="text-2xl font-extrabold text-slate-900">{recruiter.closures}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Closures</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-sm">
                    <Users size={20} className="mx-auto mb-2 text-cyan-600" />
                    <p className="text-2xl font-extrabold text-slate-900">{recruiter.joinings}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Joinings</p>
                  </div>
                  {isAdminUser && (
                    <div className="text-center p-3 bg-slate-50 rounded-sm">
                      <DollarSign size={20} className="mx-auto mb-2 text-slate-900" />
                      <p className="text-2xl font-extrabold text-slate-900">{`₹${recruiter.revenue.toLocaleString('en-IN')}`}</p>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Revenue</p>
                    </div>
                  )}
                </div>
                {/* Target vs Achievement Row */}
                {(recruiter.closure_target > 0 || recruiter.cv_target > 0) && (
                  <div className="mt-3 p-3 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Target size={14} className="text-indigo-600" />
                      <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Target vs Actual</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <p className="text-sm font-extrabold text-slate-900">{recruiter.resumes}<span className="text-slate-400 font-normal">/{recruiter.cv_target || 0}</span></p>
                        <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1">
                          <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${Math.min(100, recruiter.cv_target > 0 ? (recruiter.resumes / recruiter.cv_target) * 100 : 0)}%` }} />
                        </div>
                        <p className="text-[9px] text-slate-500 mt-0.5 uppercase">CVs</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-extrabold text-slate-900">{recruiter.interviews}<span className="text-slate-400 font-normal">/{recruiter.interview_target || 0}</span></p>
                        <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1">
                          <div className="h-1.5 rounded-full bg-amber-500" style={{ width: `${Math.min(100, recruiter.interview_target > 0 ? (recruiter.interviews / recruiter.interview_target) * 100 : 0)}%` }} />
                        </div>
                        <p className="text-[9px] text-slate-500 mt-0.5 uppercase">Interviews</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-extrabold text-slate-900">{recruiter.closures}<span className="text-slate-400 font-normal">/{recruiter.closure_target || 0}</span></p>
                        <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1">
                          <div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${Math.min(100, recruiter.closure_target > 0 ? (recruiter.closures / recruiter.closure_target) * 100 : 0)}%` }} />
                        </div>
                        <p className="text-[9px] text-slate-500 mt-0.5 uppercase">Closures</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Card view summary */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded p-4" data-testid="recruiters-summary-card">
            <h3 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-3">Aggregated Summary</h3>
            <div className={`grid ${isAdminUser ? 'grid-cols-6' : 'grid-cols-5'} gap-4 text-center`}>
              <div><p className="text-2xl font-extrabold text-slate-900">{totals.positions}</p><p className="text-xs text-slate-500 uppercase">Positions</p></div>
              <div><p className="text-2xl font-extrabold text-slate-900">{totals.resumes}</p><p className="text-xs text-slate-500 uppercase">Resumes</p></div>
              <div><p className="text-2xl font-extrabold text-slate-900">{totals.interviews}</p><p className="text-xs text-slate-500 uppercase">Interviews</p></div>
              <div><p className="text-2xl font-extrabold text-slate-900">{totals.closures}</p><p className="text-xs text-slate-500 uppercase">Closures</p></div>
              <div><p className="text-2xl font-extrabold text-slate-900">{totals.joinings}</p><p className="text-xs text-slate-500 uppercase">Joinings</p></div>
              {isAdminUser && <div><p className="text-2xl font-extrabold text-slate-900">{`₹${totals.revenue.toLocaleString('en-IN')}`}</p><p className="text-xs text-slate-500 uppercase">Revenue</p></div>}
            </div>
          </div>
        </>
      ) : (
        /* Table View */
        <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-50 border-b border-blue-100">
                <TableHead className="font-bold text-blue-900">Recruiter</TableHead>
                <TableHead className="font-bold text-blue-900">Team</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Positions</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Resumes</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Interviews</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Closures</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Joinings</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">CV Target</TableHead>
                <TableHead className="font-bold text-blue-900 text-center">Closure Target</TableHead>
                {isAdminUser && <TableHead className="font-bold text-blue-900 text-right">Revenue</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {performance.map((r) => (
                <TableRow key={r.recruiter_id} data-testid={`recruiter-row-${r.recruiter_id}`} className="hover:bg-blue-50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">{r.recruiter_name.charAt(0)}</div>
                      <span className="font-semibold text-blue-900">{r.recruiter_name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-indigo-600 font-medium">{r.team_name || '-'}</TableCell>
                  <TableCell className="text-center font-bold">{r.positions}</TableCell>
                  <TableCell className="text-center font-bold">{r.resumes}</TableCell>
                  <TableCell className="text-center font-bold">{r.interviews}</TableCell>
                  <TableCell className="text-center font-bold text-emerald-700">{r.closures}</TableCell>
                  <TableCell className="text-center font-bold text-cyan-700">{r.joinings}</TableCell>
                  <TableCell className="text-center">
                    {r.cv_target > 0 ? (
                      <span className="text-sm">{r.resumes}<span className="text-slate-400">/{r.cv_target}</span>
                        <Badge className={`ml-1.5 text-[9px] ${r.resumes >= r.cv_target ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {r.cv_target > 0 ? Math.round((r.resumes / r.cv_target) * 100) : 0}%
                        </Badge>
                      </span>
                    ) : <span className="text-xs text-slate-400">Not set</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {r.closure_target > 0 ? (
                      <span className="text-sm">{r.closures}<span className="text-slate-400">/{r.closure_target}</span>
                        <Badge className={`ml-1.5 text-[9px] ${r.closures >= r.closure_target ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {r.closure_target > 0 ? Math.round((r.closures / r.closure_target) * 100) : 0}%
                        </Badge>
                      </span>
                    ) : <span className="text-xs text-slate-400">Not set</span>}
                  </TableCell>
                  {isAdminUser && <TableCell className="text-right font-bold text-slate-900">{`₹${r.revenue.toLocaleString('en-IN')}`}</TableCell>}
                </TableRow>
              ))}
              {/* Totals row */}
              <TableRow className="bg-blue-100 border-t-2 border-blue-300" data-testid="recruiters-summary-row">
                <TableCell className="font-extrabold text-blue-900" colSpan={2}>TOTAL ({performance.length} recruiters)</TableCell>
                <TableCell className="text-center font-extrabold text-blue-900">{totals.positions}</TableCell>
                <TableCell className="text-center font-extrabold text-blue-900">{totals.resumes}</TableCell>
                <TableCell className="text-center font-extrabold text-blue-900">{totals.interviews}</TableCell>
                <TableCell className="text-center font-extrabold text-emerald-800">{totals.closures}</TableCell>
                <TableCell className="text-center font-extrabold text-cyan-800">{totals.joinings}</TableCell>
                <TableCell className="text-center text-xs text-slate-400">—</TableCell>
                <TableCell className="text-center text-xs text-slate-400">—</TableCell>
                {isAdminUser && <TableCell className="text-right font-extrabold text-blue-900">{`₹${totals.revenue.toLocaleString('en-IN')}`}</TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
